import { FormSectionHandler } from '../entity.handler';
import { UserResponse, UserUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, TimeField, EnumToFormBuilderComboValues } from '../utils';

interface EditorValues {
    smsFromTime?: string;
    smsToTime?: string;
    low?: string;
    medium?: string;
    high?: string;
}

export class UserNotiftySectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = 'bell';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.NOTIFY.${key}`, values);
    }

    applyUpdate(entity: UserResponse, payload: UserUpdateRequest): Promise<UserResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateUser(entity.id, payload));
    }

    mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        const from = ((entity.notifySettings.smsTime && entity.notifySettings.smsTime.from) || '00:00').slice(0, 5);
        const to = ((entity.notifySettings.smsTime && entity.notifySettings.smsTime.to) || '23:59').slice(0, 5);
        return Promise.resolve({
            smsFromTime: from,
            smsToTime: to,
            low: entity.notifySettings.actions.low,
            medium: entity.notifySettings.actions.medium,
            high: entity.notifySettings.actions.high,
        });
    }

    mapEditorToEntity(entity: UserUpdateRequest, values: EditorValues): Promise<UserUpdateRequest> {
        entity.notifySettings = entity.notifySettings || {} as any;
        if (values.smsFromTime || values.smsToTime) {
            entity.notifySettings.smsTime = entity.notifySettings.smsTime || {} as any;
        }

        if (values.smsFromTime) {
            entity.notifySettings.smsTime.from = values.smsFromTime;
        }

        if (values.smsToTime) {
            let value = values.smsToTime;
            // our time editors dont' allow for editing seconds, so try and detect if someone put xx:59 in and complete that to xx:59:59 or else
            // we're gonna end up with a 1 minute dead spot
            if (value.includes(':59')) {
                value = value.slice(0, 5) + ':59';
            }
            entity.notifySettings.smsTime.to = value;
        }

        if (values.low || values.high || values.medium) {
            entity.notifySettings.actions = entity.notifySettings.actions || {} as any;
            EntityUtils.mergeFields(['low', 'medium', 'high'], values, entity.notifySettings.actions);
        }
        return Promise.resolve(entity);
    }

    getFields(entity: UserResponse): Promise<FormBuilderField[]> {
        const priorityValues = EnumToFormBuilderComboValues(this.admin.i18n, 'ADMIN.EDITORS.USER.NOTIFY.ACTIONS', ['none', 'email', 'sms', 'emailsms']);
        return Promise.resolve([
            TimeField('smsFromTime', this.translate('FIELDS.SMS_FROM'), true),
            TimeField('smsToTime', this.translate('FIELDS.SMS_TO'), true),
            ComboField('low', this.translate('FIELDS.LOW_ACTION'), true, priorityValues),
            ComboField('medium', this.translate('FIELDS.MEDIUM_ACTION'), true, priorityValues),
            ComboField('high', this.translate('FIELDS.HIGH_ACTION'), true, priorityValues),
        ]).then(fields => EntityUtils.prepareBulk(!entity, ['smsFromTime', 'smsToTime', 'low', 'medium', 'high'], fields));
    }

}

